///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Maps an entity's InfoPanelData to a HoverCardSpec: what each dataset
/// shows in its hover preview. Every card follows the same grammar (see
/// HoverCard.ts); this file only decides which figure is the hero, which
/// handful of fields make the preview, and what goes in the footer. All
/// values pass through the unit system, so a buoy and a city agree on °F.
///

import { cardinalFromDeg, getTempColor } from "@/earthbrowser/dataset-distribution/locations/CityWeatherStyle"
import { stormCategoryLabel } from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable"
import { stormCategoryHex } from "@/earthbrowser/dataset-distribution/hurricanes/stormDisplay"
import { fireAcreage, isFireActive } from "@/earthbrowser/dataset-distribution/wildfires/WildfiresTable"
import { type InfoPanelData } from "@/earthbrowser/datasets/InfoPanelProvider"
import { formatAltitude, formatDepth, formatDistance, formatFireArea, formatWaveHeight } from "@/earthbrowser/utils/unitFormatters"
import { MeasurementType, shouldUseMetric } from "@/earthbrowser/utils/UnitSystem"
import { type InfoWindowTheme } from "@/redux/userInterfaceSlice"
import { datasetUrl } from "@/utils/datasetUrl"
import { formatAgeCompact, type HoverCardBadge, type HoverCardField, type HoverCardSpec } from "./HoverCard"

/** Dataset accents; the same hues the focused React panels use for pill and border */
export const cHoverAccent: Record<InfoWindowTheme, string> = {
	location: "#22d3ee",
	satellite: "#60a5fa",
	volcano: "#fb923c",
	earthquake: "#f87171",
	buoy: "#22d3ee",
	webcam: "#e879f9",
	radar: "#34d399",
	wildfire: "#fb7185",
	hurricane: "#38bdf8",
	alert: "#fbbf24",
	placemark: "#e5484d",
	synoptic: "#c4b5fd",
	default: "#93c5fd",
}

const cMuted = "#94a3b8"

// Webcam category palette (design-system/03-hud-canvas.md), CSS variants of
// the marker dome colors in WebcamNode
const cWebcamCategoryColors: Record<string, string> = {
	traffic: "#d194e0",
	scenic: "#d946ef",
	weather: "#ed4799",
	marine: "#fa7085",
	aviation: "#c773ff",
	wildlife: "#73e673",
}

const cAlertSeverityColors: Record<string, string> = {
	Extreme: "#e879f9",
	Severe: "#f87171",
	Moderate: "#fbbf24",
	Minor: "#38bdf8",
	Unknown: cMuted,
}

const cQuakeAlertColors: Record<string, string> = {
	green: "#4ade80",
	yellow: "#fde047",
	orange: "#fdba74",
	red: "#fca5a5",
}

const cOrbitColors: Record<string, string> = {
	LEO: "#60a5fa",
	MEO: "#a855f7",
	GEO: "#facc15",
	HEO: "#fb923c",
}

// ============================================================================
// Formatting
// ============================================================================

function formatLat(lat: number): string {
	return `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? "N" : "S"}`
}

function formatLon(lon: number): string {
	return `${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? "E" : "W"}`
}

function coords(lat: number, lon: number): string {
	return `${formatLat(lat)}, ${formatLon(lon)}`
}

/** Whole-degree temperature in display units with its unit ("43°F") */
function temp(celsius: number): string {
	return shouldUseMetric(MeasurementType.TEMPERATURE)
		? `${Math.round(celsius)}°C`
		: `${Math.round(celsius * 9 / 5 + 32)}°F`
}

/** Whole-number wind speed in display units ("12 mph") */
function wind(mps: number): string {
	return shouldUseMetric(MeasurementType.SPEED)
		? `${Math.round(mps * 3.6)} km/h`
		: `${Math.round(mps * 2.237)} mph`
}

const cKnotToMps = 0.514444

function formatTimeAgo(isoTime: string): string {
	const elapsed = Date.now() - new Date(isoTime).getTime()
	const mins = Math.floor(elapsed / 60_000)
	if (mins < 1) return "just now"
	if (mins < 60) return `${mins}m ago`
	const hrs = Math.floor(mins / 60)
	if (hrs < 24) return `${hrs}h ${mins % 60}m ago`
	const days = Math.floor(hrs / 24)
	return `${days}d ${hrs % 24}h ago`
}

/** "3h" / "2d" left until a future instant, "expired" once past */
function formatUntil(isoTime: string): string {
	const remaining = new Date(isoTime).getTime() - Date.now()
	if (remaining <= 0) return "expired"
	const mins = Math.floor(remaining / 60_000)
	if (mins < 60) return `${mins}m`
	const hrs = Math.floor(mins / 60)
	if (hrs < 24) return `${hrs}h`
	return `${Math.floor(hrs / 24)}d`
}

function classifyOrbit(altKm: number, eccentricity: number): string {
	if (eccentricity > 0.25) return "HEO"
	if (altKm > 33000) return "GEO"
	if (altKm > 2000) return "MEO"
	return "LEO"
}

// ============================================================================
// Spec builder
// ============================================================================

export function buildHoverCardSpec(panel: InfoPanelData): HoverCardSpec {
	const accent = cHoverAccent[panel.theme ?? "default"] ?? cHoverAccent.default
	const base: HoverCardSpec = { accent, title: panel.title, subtitle: panel.subtitle }
	const data = panel.contentData

	switch (data.type) {
		case "location": {
			const w = data.weather
			const nearby = data.nearby
			const info = data.locationInfo
			if (!w) {
				return { ...base, footer: { left: coords(info.lat, info.lon) } }
			}
			const fields: HoverCardField[] = []
			if (w.wind_speed_mps != null) {
				const dir = w.wind_dir_deg != null ? `${cardinalFromDeg(w.wind_dir_deg)} ` : ""
				const gust = w.wind_gust_mps != null ? ` g${wind(w.wind_gust_mps).split(" ")[0]}` : ""
				fields.push({ label: "Wind", value: `${dir}${wind(w.wind_speed_mps)}${gust}` })
			}
			if (w.humidity_pct != null) fields.push({ label: "Humidity", value: `${Math.round(w.humidity_pct)}%` })
			if (w.pressure_hpa != null) {
				const trend = w.pressure_trend_3h_hpa != null && Math.abs(w.pressure_trend_3h_hpa) >= 1
					? (w.pressure_trend_3h_hpa > 0 ? " ↑" : " ↓")
					: ""
				fields.push({ label: "Pressure", value: `${Math.round(w.pressure_hpa)} hPa${trend}` })
			}
			if (w.dewpoint_c != null) fields.push({ label: "Dew point", value: temp(w.dewpoint_c) })
			// Cross-dataset context keeps its own hue so it reads as a pointer
			// to another layer, not a property of the city
			if (nearby && nearby.webcamCount > 0) {
				const name = nearby.nearestWebcamName ?? ""
				fields.push({
					label: nearby.webcamCount > 1 ? `${nearby.webcamCount} webcams` : "Webcam",
					value: name, wide: true, labelColor: cHoverAccent.webcam,
				})
			}
			if (nearby?.buoyWaterTempC != null) {
				const dist = nearby.buoyKm != null ? ` · ${formatDistance(nearby.buoyKm)}` : ""
				fields.push({ label: "Sea", value: `${temp(nearby.buoyWaterTempC)}${dist}`, wide: true, labelColor: "#5eead4" })
			}
			if (nearby?.auroraChancePct != null) {
				fields.push({ label: "Aurora", value: `${nearby.auroraChancePct}% chance tonight`, wide: true, labelColor: "#4ade80" })
			}
			const feels = w.feels_like_c != null && w.temp_c != null && Math.abs(w.feels_like_c - w.temp_c) >= 2
				? ` · feels ${temp(w.feels_like_c)}`
				: ""
			return {
				...base,
				hero: w.temp_c != null
					? { value: temp(w.temp_c), caption: `${w.weather_desc}${feels}`, color: getTempColor(w.temp_c, 0.98) }
					: undefined,
				fields,
				footer: { left: coords(info.lat, info.lon) },
			}
		}

		case "satellite": {
			const sat = data.satellite
			const live = data.livePosition
			const altKm = live?.alt ?? 0
			const orbit = classifyOrbit(altKm, sat.eccentricity)
			const speed = data.liveSpeed
			return {
				...base,
				subtitle: `NORAD ${sat.norad_cat_id}`,
				badges: [{ text: orbit, color: cOrbitColors[orbit] ?? cMuted }],
				hero: live
					? { value: `${Math.round(altKm).toLocaleString()} km`, caption: speed != null ? `altitude · ${speed.toFixed(2)} km/s` : "altitude" }
					: undefined,
				fields: [
					{ label: "Inclination", value: `${sat.inclination.toFixed(1)}°` },
					{ label: "Period", value: `${(1440 / sat.mean_motion).toFixed(1)} min` },
					{ label: "Eccentricity", value: sat.eccentricity < 0.001 ? sat.eccentricity.toExponential(2) : sat.eccentricity.toFixed(4) },
					{ label: "RAAN", value: `${sat.ra_of_asc_node.toFixed(1)}°` },
				],
				footer: live ? { left: coords(live.lat, live.lon) } : undefined,
			}
		}

		case "earthquake": {
			const eq = data.earthquake
			const badges: HoverCardBadge[] = []
			if (eq.alert) badges.push({ text: `${eq.alert} alert`, color: cQuakeAlertColors[eq.alert] ?? cQuakeAlertColors.green })
			if (eq.tsunami > 0) badges.push({ text: "Tsunami", color: "#7dd3fc" })
			const fields: HoverCardField[] = []
			if (eq.sig != null) fields.push({ label: "Significance", value: String(eq.sig) })
			fields.push({ label: "Status", value: eq.status === "reviewed" ? "Reviewed" : "Automatic" })
			if (eq.felt != null) fields.push({ label: "Felt", value: `${eq.felt.toLocaleString()} reports` })
			if (eq.mmi != null) fields.push({ label: "Intensity", value: `MMI ${eq.mmi.toFixed(1)}` })
			return {
				...base,
				badges,
				hero: {
					value: `M ${eq.mag.toFixed(1)}`,
					caption: `${eq.mag_type ? `${eq.mag_type} · ` : ""}${formatDepth(eq.depth_km)} deep`,
					color: accent,
				},
				fields,
				footer: { left: coords(eq.lat, eq.lon), right: formatTimeAgo(eq.event_time) },
			}
		}

		case "volcano": {
			const v = data.volcano
			const region = [v.country, v.region !== v.country ? v.region : null].filter(Boolean).join(", ")
			const fields: HoverCardField[] = []
			if (v.epoch) fields.push({ label: "Epoch", value: v.epoch })
			if (v.landform) fields.push({ label: "Landform", value: v.landform })
			if (v.tectonicSetting) fields.push({ label: "Setting", value: v.tectonicSetting, wide: true })
			return {
				...base,
				subtitle: region || panel.subtitle,
				badges: [{ text: v.type || "Volcano", color: accent }],
				hero: v.elevation
					? { value: formatAltitude(v.elevation), caption: "summit elevation" }
					: undefined,
				fields,
				footer: { left: coords(v.latitude, v.longitude), right: v.lastEruption ? `Last erupted ${v.lastEruption}` : undefined },
			}
		}

		case "buoy": {
			const b = data.buoy
			const obs = b.latest_observation
			const kind = b.dart ? "DART" : (b.type?.toUpperCase() ?? "BUOY")
			const fields: HoverCardField[] = []
			if (obs?.air_temp != null) fields.push({ label: "Air", value: temp(obs.air_temp) })
			if (obs?.water_temp != null) fields.push({ label: "Water", value: temp(obs.water_temp) })
			if (obs?.wind_speed != null) {
				const dir = obs.wind_direction != null ? `${cardinalFromDeg(obs.wind_direction)} ` : ""
				const gust = obs.wind_gust != null ? ` g${wind(obs.wind_gust).split(" ")[0]}` : ""
				fields.push({ label: "Wind", value: `${dir}${wind(obs.wind_speed)}${gust}` })
			}
			if (obs?.pressure != null) fields.push({ label: "Pressure", value: `${obs.pressure.toFixed(1)} hPa` })
			if (obs?.visibility != null) fields.push({ label: "Visibility", value: `${obs.visibility.toFixed(1)} nmi` })
			if (obs?.dewpoint != null) fields.push({ label: "Dew point", value: temp(obs.dewpoint) })
			const hero = obs?.wave_height != null
				? { value: formatWaveHeight(obs.wave_height), caption: obs.wave_period != null ? `waves · ${obs.wave_period.toFixed(0)} s period` : "waves" }
				: b.dart
					? { value: "DART", caption: "tsunami detection buoy" }
					: undefined
			return {
				...base,
				subtitle: `${kind} · ${b.station_id}${b.owner ? ` · ${b.owner}` : ""}`,
				badges: b.dart ? [{ text: "DART", color: "#f472b6" }] : undefined,
				hero,
				fields,
				footer: { left: coords(b.lat, b.lon), right: obs?.time ? `Updated ${formatAgeCompact(obs.time)}` : undefined },
			}
		}

		case "webcam": {
			const cam = data.webcam
			// Cache-bust by last ingest success, matching the focused React panel,
			// so we pick up newer thumbs without hammering the CDN
			const stamp = cam.last_success ? new Date(cam.last_success).getTime() : 0
			const badges: HoverCardBadge[] = []
			if (cam.stream_url && cam.stream_type) badges.push({ text: "● Live", color: "#f87171" })
			badges.push({ text: cam.category, color: cWebcamCategoryColors[cam.category] ?? accent })
			return {
				...base,
				media: {
					url: `${datasetUrl(cam.thumb_url)}?eb=${stamp}`,
					aspect: 16 / 9,
					badges,
					showCapturedAge: true,
				},
				footer: { left: cam.attribution },
			}
		}

		case "wildfire": {
			const fire = data.wildfire
			const acres = fireAcreage(fire)
			const active = isFireActive(fire)
			const contained = fire.percent_contained
			const fields: HoverCardField[] = []
			if (contained != null) fields.push({ label: "Contained", value: `${Math.round(Math.min(100, Math.max(0, contained)))}%` })
			if (fire.personnel != null) fields.push({ label: "Crew", value: fire.personnel.toLocaleString() })
			if (fire.fire_cause) fields.push({ label: "Cause", value: fire.fire_cause, wide: true })
			if (fire.fire_behavior) fields.push({ label: "Behavior", value: fire.fire_behavior, wide: true })
			return {
				...base,
				badges: [active
					? { text: "Active", color: "#fca5a5" }
					: { text: "Contained", color: "#4ade80" }],
				hero: acres != null
					? { value: formatFireArea(acres).replace(/\s*\(.*\)$/, ""), caption: "burned", color: accent }
					: undefined,
				fields,
				footer: {
					left: fire.discovered ? `Discovered ${formatAgeCompact(fire.discovered)}` : undefined,
					right: `Updated ${formatAgeCompact(fire.date_current)}`,
				},
			}
		}

		case "hurricane": {
			const storm = data.storm
			const category = stormCategoryLabel(storm)
			const windKt = storm.max_wind_kt
			const fields: HoverCardField[] = []
			if (storm.mslp_mb != null) fields.push({ label: "Pressure", value: `${Math.round(storm.mslp_mb)} hPa` })
			if (storm.movement_speed_kt != null) {
				const dir = storm.movement_dir != null ? `${cardinalFromDeg(storm.movement_dir)} ` : ""
				fields.push({ label: "Moving", value: `${dir}${wind(storm.movement_speed_kt * cKnotToMps)}` })
			}
			if (storm.advisory_num) fields.push({ label: "Advisory", value: `#${storm.advisory_num}` })
			const basin = panel.subtitle ?? storm.basin
			return {
				...base,
				title: storm.name,
				subtitle: basin ? `${storm.classification} · ${basin}` : storm.classification,
				badges: [{ text: category, color: stormCategoryHex(storm.category, windKt) }],
				hero: windKt != null
					? {
						value: wind(windKt * cKnotToMps),
						// Gusts share the unit just shown, so the number alone
						caption: storm.gust_kt != null ? `sustained · gusts ${wind(storm.gust_kt * cKnotToMps).split(" ")[0]}` : "sustained winds",
						color: stormCategoryHex(storm.category, windKt),
					}
					: undefined,
				fields,
				footer: { left: coords(storm.lat, storm.lon), right: `Advisory ${formatAgeCompact(storm.advisory_date)}` },
			}
		}

		case "alert": {
			const alert = data.alert
			const fields: HoverCardField[] = []
			if (alert.urgency) fields.push({ label: "Urgency", value: alert.urgency })
			if (alert.certainty) fields.push({ label: "Certainty", value: alert.certainty })
			if (alert.onset) fields.push({ label: "Onset", value: new Date(alert.onset).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }) })
			const remaining = alert.expires ? formatUntil(alert.expires) : null
			return {
				...base,
				badges: [
					{ text: alert.severity, color: cAlertSeverityColors[alert.severity] ?? cMuted },
					...(alert.message_type && alert.message_type !== "Alert" ? [{ text: alert.message_type, color: cMuted }] : []),
				],
				hero: remaining
					? (remaining === "expired"
						? { value: "Expired", caption: alert.expires ? formatAgeCompact(alert.expires) : undefined }
						: { value: remaining, caption: "until it expires" })
					: undefined,
				fields,
				footer: { left: alert.sender_name ?? alert.source, right: `Sent ${formatAgeCompact(alert.sent)}` },
			}
		}

		case "placemark": {
			const placemark = data.placemark
			const member = data.memberId ? placemark.locations.find(l => l.id === data.memberId) : undefined
			const anchor = member ?? placemark.locations.find(l => l.id === placemark.anchorId) ?? placemark.locations[0]
			const fields: HoverCardField[] = []
			if (!member && placemark.locations.length > 1) fields.push({ label: "Locations", value: String(placemark.locations.length) })
			if (placemark.tags.length > 0) fields.push({ label: "Tags", value: placemark.tags.join(", "), wide: placemark.locations.length <= 1 })
			const note = member?.note ?? placemark.note
			if (note) fields.push({ label: "Note", value: note, wide: true })
			return {
				...base,
				fields,
				footer: anchor ? { left: coords(anchor.lat, anchor.lon), right: `Saved ${formatAgeCompact(placemark.updatedAt)}` } : undefined,
			}
		}

		case "pressure_center": {
			const c = data.center
			const low = c.centerType === "L"
			const fields: HoverCardField[] = []
			if (Number.isFinite(c.tendency3h)) {
				const t = c.tendency3h
				fields.push({ label: "3h trend", value: `${t > 0 ? "+" : ""}${t.toFixed(1)} hPa` })
			}
			if (c.motion) {
				fields.push({ label: "Moving", value: c.motion.speedKmh < 8 ? "nearly stationary" : `${cardinalFromDeg(c.motion.bearingDeg)} ${wind(c.motion.speedKmh / 3.6)}` })
			}
			if (c.extreme) {
				const when = new Date(c.extreme.timeMs)
				const day = when.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" })
				const hour = when.getUTCHours().toString().padStart(2, "0")
				fields.push({ label: low ? "Deepest" : "Peak", value: `${Math.round(c.extreme.pressureHpa)} hPa ${day} ${hour}Z`, wide: true })
			}
			return {
				...base,
				title: low ? "Low pressure" : "High pressure",
				subtitle: panel.subtitle,
				hero: { value: `${Math.round(c.pressureHpa)} hPa`, caption: low ? "central pressure" : "central pressure", color: accent },
				fields,
				footer: { left: coords(c.lat, c.lon) },
			}
		}

		case "radar":
			return {
				...base,
				subtitle: data.stationId,
				badges: [{ text: "Radar", color: accent }],
			}
	}

	return base
}
